import React, { createContext, useContext, useState, useEffect } from 'react';
import { ADMIN_EMAIL, ADMIN_PASSWORD } from '../config/constants';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('liberado_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [isTestMode, setIsTestMode] = useState(() => {
    return localStorage.getItem('liberado_testmode') === 'true';
  });

  const [simulatedTime, setSimulatedTime] = useState(() => {
    const saved = localStorage.getItem('liberado_simulated_time');
    return saved ? parseInt(saved) : Date.now();
  });

  const [techToS, setTechToS] = useState(() => {
    const saved = localStorage.getItem('liberado_tech_tos');
    return saved ? parseInt(saved) : 1;
  });

  // Initialize fresh admin account ONLY on first load
  useEffect(() => {
    const isInitialized = localStorage.getItem('liberado_initialized');
    
    if (!isInitialized) {
      // Wipe old user data
      localStorage.removeItem('liberado_all_users');
      localStorage.removeItem('users');
      localStorage.removeItem('allUsers');
      
      // Create fresh admin account
      const adminAccount = {
        id: 'admin-001',
        email: ADMIN_EMAIL,
        password: ADMIN_PASSWORD,
        role: 'admin',
        name: 'Admin',
        createdAt: new Date().toISOString(),
      };
      
      // Save admin to all users list
      localStorage.setItem('liberado_all_users', JSON.stringify([adminAccount]));
      localStorage.setItem('liberado_initialized', 'true');
    }
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem('liberado_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('liberado_user');
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem('liberado_testmode', isTestMode);
  }, [isTestMode]);

  useEffect(() => {
    localStorage.setItem('liberado_simulated_time', simulatedTime);
  }, [simulatedTime]);

  useEffect(() => {
    localStorage.setItem('liberado_tech_tos', techToS);
  }, [techToS]);

  const saveUserToAllUsers = (userData) => {
    const allUsers = getAllUsers();
    const existingIndex = allUsers.findIndex(u => u.id === userData.id);
    
    if (existingIndex >= 0) {
      allUsers[existingIndex] = userData;
    } else {
      allUsers.push(userData);
    }
    
    localStorage.setItem('liberado_all_users', JSON.stringify(allUsers));
  };

  const getAllUsers = () => {
    const saved = localStorage.getItem('liberado_all_users');
    return saved ? JSON.parse(saved) : [];
  };

  const createFakeUser = (name, email, role) => {
    const fakeUser = {
      id: Date.now(),
      name,
      email,
      role,
      createdAt: new Date().toISOString(),
    };
    saveUserToAllUsers(fakeUser);
    return fakeUser;
  };

  const login = (email, password) => {
    // Check against stored users
    const allUsers = getAllUsers();
    const foundUser = allUsers.find(u => 
      u.email.toLowerCase() === email.toLowerCase() && u.password === password
    );
    
    if (foundUser) {
      // Don't store password in session
      const { password: _, ...userWithoutPassword } = foundUser;
      setUser(userWithoutPassword);
      return { success: true, user: userWithoutPassword };
    }
    
    return { success: false, error: 'Invalid credentials' };
  };

  const signup = (email, password, role, name) => {
    // Simulated signup
    const mockUser = {
      id: Date.now(),
      email,
      password,
      role,
      name,
      createdAt: new Date().toISOString(),
    };
    saveUserToAllUsers(mockUser);
    
    const { password: _, ...userWithoutPassword } = mockUser;
    setUser(userWithoutPassword);
    return { success: true, user: userWithoutPassword };
  };

  const resetPassword = (email, resetCode, newPassword) => {
    const allUsers = getAllUsers();
    const userIndex = allUsers.findIndex(u => u.email.toLowerCase() === email.toLowerCase());
    
    if (userIndex === -1) {
      return { success: false, error: 'User not found' };
    }
    
    // Update password
    allUsers[userIndex].password = newPassword;
    localStorage.setItem('liberado_all_users', JSON.stringify(allUsers));
    
    return { success: true };
  };

  const logout = () => {
    setUser(null);
  };

  const toggleTestMode = () => {
    setIsTestMode(prev => !prev);
  };

  const timeTravel = (hours) => {
    setSimulatedTime(prev => prev + (hours * 60 * 60 * 1000));
  };

  const resetTime = () => {
    setSimulatedTime(Date.now());
  };

  const changeTechToS = (level) => {
    if (level >= 1 && level <= 3) {
      setTechToS(level);
    }
  };

  const switchRole = (newRole) => {
    if (user && ['clinic', 'technician', 'admin'].includes(newRole)) {
      setUser({ ...user, role: newRole });
    }
  };

  const getTosInfo = (level) => {
    const tosData = {
      1: { pay: 18, scopes: 'In-Room, Treatment, Lab' },
      2: { pay: 21, scopes: 'ToS1 + Surgery' },
      3: { pay: 24, scopes: 'ToS2 + Emergency/Urgent + Specialty' },
    };
    return tosData[level] || tosData[1];
  };

  return (
    <AuthContext.Provider value={{
      user,
      isTestMode,
      simulatedTime,
      techToS,
      login,
      signup,
      logout,
      resetPassword,
      toggleTestMode,
      timeTravel,
      resetTime,
      changeTechToS,
      switchRole,
      getTosInfo,
      isAuthenticated: !!user,
      getAllUsers,
      createFakeUser,
      saveUserToAllUsers,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
